import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";

const AGE_GATE_STORAGE_KEY = "kratown_age_verification";
const AGE_GATE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const KRATOWN_LOGO_PATH = "/manus-storage/kratown-logo-light_477b8b41.png";

function hasCurrentAgeAcceptance() {
  try {
    const stored = window.localStorage.getItem(AGE_GATE_STORAGE_KEY);
    if (!stored) return false;
    const record = JSON.parse(stored) as { expiresAt?: number };
    return typeof record.expiresAt === "number" && record.expiresAt > Date.now();
  } catch {
    return false;
  }
}

export function AgeVerificationModal() {
  const [ready, setReady] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(!hasCurrentAgeAcceptance());
    setReady(true);
  }, []);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    const previousTouchAction = document.body.style.touchAction;
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.touchAction = previousTouchAction;
    };
  }, [open]);

  const confirmAge = () => {
    const acceptedAt = Date.now();
    try {
      window.localStorage.setItem(AGE_GATE_STORAGE_KEY, JSON.stringify({ acceptedAt, expiresAt: acceptedAt + AGE_GATE_TTL_MS }));
    } catch {
      // Access still remains available for this browser session if storage is unavailable.
    }
    setOpen(false);
  };

  const exitWebsite = () => {
    window.location.assign("https://www.google.com");
  };

  if (!ready) return null;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => nextOpen && setOpen(true)}>
      <DialogContent
        showCloseButton={false}
        aria-describedby="age-verification-description"
        aria-modal="true"
        overlayClassName="bg-[#020604]/84 backdrop-blur-[10px]"
        className="max-h-[calc(100dvh-1rem)] max-w-[calc(100%-1rem)] overflow-x-hidden overflow-y-auto border border-[#c2ff45]/25 bg-[#06110f] p-0 text-white shadow-[0_25px_120px_rgba(0,0,0,0.65)] sm:max-h-[calc(100dvh-2rem)] sm:max-w-xl"
        onEscapeKeyDown={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <div className="relative overflow-hidden px-5 py-5 sm:px-10 sm:py-11">
          <div className="absolute -left-20 top-0 h-36 w-36 rounded-full bg-[#c2ff45]/14 blur-3xl sm:h-48 sm:w-48" aria-hidden="true" />
          <div className="absolute -right-16 bottom-0 h-36 w-36 rounded-full bg-[#36d8ff]/12 blur-3xl sm:h-48 sm:w-48" aria-hidden="true" />
          <DialogHeader className="relative text-left">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/20 bg-white shadow-[0_0_32px_rgba(194,255,69,0.2)] sm:h-14 sm:w-14">
                <img
                  src={KRATOWN_LOGO_PATH}
                  alt="Kratown Kratom"
                  className="h-full w-full object-contain"
                />
              </div>
              <div>
                <p className="text-xl font-semibold tracking-[0.08em] text-white sm:text-2xl">KRATOWN</p>
                <p className="mt-0.5 text-[10px] uppercase tracking-[0.18em] text-white/52 sm:mt-1 sm:text-[11px] sm:tracking-[0.24em]">Botanical goods for your everyday ritual</p>
              </div>
            </div>
            <div className="mt-6 flex items-center gap-2 text-[#d9ff8a] sm:mt-10 sm:gap-3">
              <ShieldCheck className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden="true" />
              <p className="text-xs uppercase tracking-[0.2em]">21+ access</p>
            </div>
            <DialogTitle className="mt-4 flex flex-col font-serif leading-[0.95] text-white">
              <span className="text-base font-medium tracking-[0.02em] text-[#d9ff8a] sm:text-xl">Welcome to Kratown</span>
              <span className="mt-1.5 text-4xl sm:mt-2 sm:text-6xl">Age Verification</span>
            </DialogTitle>
            <DialogDescription id="age-verification-description" className="mt-4 max-w-lg text-[15px] leading-6 text-white/72 sm:mt-5 sm:text-lg sm:leading-8">
              You must be at least 21 years of age to enter this website. By continuing, you confirm you meet the legal age requirement and understand that Kratown products cannot be shipped to certain states and local jurisdictions due to applicable laws.
            </DialogDescription>
          </DialogHeader>
          <div className="relative mt-5 grid grid-cols-2 gap-2 sm:mt-10 sm:gap-3">
            <Button type="button" onClick={confirmAge} className="min-h-12 border border-[#c2ff45]/55 bg-[#b8ff2e] px-2 text-sm font-semibold text-[#07110f] shadow-[0_0_36px_rgba(184,255,46,0.28)] hover:bg-[#d2ff74] focus-visible:ring-[#d2ff74] sm:min-h-14 sm:px-4 sm:text-base">
              I am 21 or Older
            </Button>
            <Button type="button" variant="outline" onClick={exitWebsite} className="min-h-12 border-white/35 bg-transparent px-2 text-sm font-medium text-white/88 hover:border-white/55 hover:bg-white/10 hover:text-white sm:min-h-14 sm:px-4 sm:text-base">
              Exit Website
            </Button>
          </div>
          <p className="relative mt-5 text-xs leading-5 text-white/60 sm:mt-6 sm:text-sm sm:leading-6">
            By entering this website you certify that you are at least 21 years of age and agree to comply with all applicable laws regarding kratom in your jurisdiction.
          </p>
          <p className="relative mt-3 rounded-lg border border-white/10 bg-black/15 px-3 py-2 text-xs leading-5 text-white/62 sm:hidden">
            <span className="font-semibold text-white/84">Shipping restrictions:</span> Orders to prohibited or restricted jurisdictions cannot be fulfilled.
          </p>
          <a href="/shipping-restrictions" target="_blank" rel="noreferrer" className="relative mt-3 inline-flex text-sm font-medium text-[#d9ff8a] underline-offset-4 hover:text-white hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c2ff45] focus-visible:ring-offset-2 focus-visible:ring-offset-[#06110f] sm:mt-4">
            Read shipping restrictions
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
